"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/** useResizeObserver hook based on modified version of https://github.com/ZeeCoder/use-resize-observer */
const react_1 = __importDefault(require("react"));
function useResizeObserver(opts = {}) {
    // `defaultRef` Has to be non-conditionally declared here whether or not it'll
    // be used as that's how hooks work.
    // @see https://reactjs.org/docs/hooks-rules.html#explanation
    const defaultRef = react_1.default.useRef(null);
    // Saving the callback as a ref. With this, I don't need to put onResize in the
    // effect dep array, and just passing in an anonymous function without memoising
    // will not reinstantiate the hook's ResizeObserver
    const onResize = opts.onResize;
    const onResizeRef = react_1.default.useRef(undefined);
    onResizeRef.current = onResize;
    // Using a single instance throughought the hook's lifetime
    const resizeObserverRef = react_1.default.useRef();
    const ref = opts.ref || defaultRef;
    const [size, setSize] = react_1.default.useState({
        width: undefined,
        height: undefined,
        top: undefined,
        left: undefined,
        bottom: undefined,
        right: undefined
    });
    // Using a ref to track the previous width / height to avoid unnecessary renders
    const previous = react_1.default.useRef({
        width: undefined,
        height: undefined,
        top: undefined,
        left: undefined,
        bottom: undefined,
        right: undefined
    });
    const requestRef = react_1.default.useRef();
    react_1.default.useEffect(() => {
        if (resizeObserverRef.current) {
            return;
        }
        if (window && window.ResizeObserver) {
            resizeObserverRef.current = new ResizeObserver((entries) => {
                requestRef.current = requestAnimationFrame(() => {
                    if (!Array.isArray(entries)) {
                        return;
                    }
                    // Since we only observe the one element, we don't need to loop over the
                    // array
                    if (!entries.length) {
                        return;
                    }
                    const entry = entries[0];
                    // `Math.round` is in line with how CSS resolves sub-pixel values
                    const newWidth = Math.round(entry.contentRect.width);
                    const newHeight = Math.round(entry.contentRect.height);
                    const newTop = Math.round(entry.contentRect.top);
                    const newLeft = Math.round(entry.contentRect.left);
                    const newBottom = Math.round(entry.contentRect.bottom);
                    const newRight = Math.round(entry.contentRect.right);
                    if (previous.current.width !== newWidth ||
                        previous.current.height !== newHeight ||
                        previous.current.top !== newTop ||
                        previous.current.left !== newLeft ||
                        previous.current.bottom !== newBottom ||
                        previous.current.right !== newRight) {
                        const newSize = {
                            width: newWidth,
                            height: newHeight,
                            top: newTop,
                            left: newLeft,
                            bottom: newBottom,
                            right: newRight
                        };
                        if (onResizeRef.current) {
                            onResizeRef.current(newSize);
                        }
                        else {
                            previous.current.width = newWidth;
                            previous.current.height = newHeight;
                            previous.current.top = newTop;
                            previous.current.left = newLeft;
                            previous.current.bottom = newBottom;
                            previous.current.right = newRight;
                            setSize(newSize);
                        }
                    }
                });
            });
        }
        return () => {
            if (requestRef && requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, []);
    react_1.default.useEffect(() => {
        var _a;
        if (typeof ref !== 'object' ||
            ref === null ||
            !(ref.current instanceof Element)) {
            return;
        }
        const element = ref.current;
        (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.observe(element);
        return () => { var _a; return (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.unobserve(element); };
    }, [ref]);
    return react_1.default.useMemo(() => ({
        ref,
        width: size.width,
        height: size.height,
        top: size.top,
        left: size.left,
        bottom: size.bottom,
        right: size.right
    }), [ref, size.width, size.height, size.top, size.left, size.bottom, size.right]);
}
exports.default = useResizeObserver;
//# sourceMappingURL=useResizeObserver.js.map